<template>
    <div class="modal-container modal-container--wide">
        <div class="modal-header">
            <div class="icon-container">
                <PhShieldCheck :size="24" class="text-gray-500" />
            </div>
            <div class="header-text">
                <h2>Verify Domain</h2>
                <p>Verify ownership of {{ domainName }}</p>
            </div>
        </div>

        <div class="modal-body">
            <div class="verification-info">
                <p class="info-text">
                    To verify your domain, add the following TXT record to your DNS settings:
                </p>

                <div class="record-box">
                    <div class="record-row">
                        <span class="record-label">Type:</span>
                        <span class="record-value">TXT</span>
                    </div>
                    <div class="record-row">
                        <span class="record-label">Host:</span>
                        <span class="record-value">@</span>
                    </div>
                    <div class="record-row">
                        <span class="record-label">Value:</span>
                        <div class="value-with-copy">
                            <code>{{ verificationCode }}</code>
                            <button type="button" class="copy-btn" @click="copyCode">
                                <PhCopy :size="16" />
                            </button>
                        </div>
                    </div>
                </div>

                <p class="note-text">
                    DNS changes may take up to 48 hours to propagate. Click "Verify" once you've added the record.
                </p>
            </div>
        </div>

        <div class="modal-footer">
            <PrimeButton label="Cancel" @click="closeDialog" class="p-btn-root_secondary" :disabled="isLoading" />
            <PrimeButton label="Verify" @click="handleVerify" :loading="isLoading" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { PhShieldCheck, PhCopy } from '@phosphor-icons/vue';
import { businessService } from '~/services/business-service';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.VERIFY_BUSINESS_DOMAIN);
const { showSuccess, showError } = useGlobalToast();
const isLoading = ref(false);

const companyId = computed(() => dialogRef.value?.data?.companyId);
const domain = computed(() => dialogRef.value?.data?.domain);
const domainName = computed(() => domain.value?.name || domain.value?.domain || '');
const verificationCode = computed(() => domain.value?.verification_code || `privacybee-verify=${domain.value?.id}`);

const copyCode = async () => {
    try {
        await navigator.clipboard.writeText(verificationCode.value);
        showSuccess('Copied', 'Verification code copied to clipboard');
    } catch {
        showError('Error', 'Failed to copy to clipboard');
    }
};

const closeDialog = () => {
    dialogRef.value.close();
};

const handleVerify = async () => {
    isLoading.value = true;
    try {
        await businessService.verifyDomain(companyId.value, domain.value.id);
        showSuccess('Success', 'Domain verified successfully');
        dialogRef.value.close({ updated: true });
    } catch (error: any) {
        showError('Error', error.message || 'Domain verification failed. Please ensure the TXT record is correctly configured.');
    } finally {
        isLoading.value = false;
    }
};
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/business-form-modal';

.modal-body {
    .verification-info {
        .info-text {
            @apply text-sm text-zinc-700 mb-4;
        }

        .record-box {
            @apply bg-zinc-50 border border-zinc-200 rounded-lg p-4 space-y-2;

            .record-row {
                @apply flex items-start gap-2;

                .record-label {
                    @apply text-sm font-medium text-zinc-500 w-16;
                }

                .record-value {
                    @apply text-sm text-zinc-900 font-mono;
                }

                .value-with-copy {
                    @apply flex items-center gap-2 flex-1;

                    code {
                        @apply text-sm text-zinc-900 font-mono bg-white px-2 py-1 rounded border break-all;
                    }

                    .copy-btn {
                        @apply p-1 rounded hover:bg-zinc-200 text-zinc-500 hover:text-zinc-700 flex-shrink-0;
                    }
                }
            }
        }

        .note-text {
            @apply text-xs text-zinc-500 mt-4;
        }
    }
}
</style>
